// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';

import { CombinedState } from 'reducers';
import AnnotationMenuComponent, { Actions } from 'components/reference-annotation-page/top-bar/annotation-menu';
import {
    saveAnnotationsAsync,
    setForceExitAnnotationFlag as setForceExitAnnotationFlagAction,
    removeAnnotationsAsync as removeAnnotationsAsyncAction,
} from 'actions/annotation-actions';
import { exportActions } from 'actions/export-actions';
import { importActions } from 'actions/import-actions';
import { getCore, ReferenceJob, ReferenceJobStage, ReferenceJobState } from 'cvat-core-wrapper';
import { message } from 'antd';
import { updateReferenceJobAsync } from 'actions/reference-jobs-actions';

const core = getCore();

interface StateToProps {
    jobInstance: ReferenceJob;
    stopFrame: number;
}

interface DispatchToProps {
    showExportModal: (jobInstance: ReferenceJob) => void;
    showExportBlenderModal: (jobInstance: ReferenceJob) => void;
    showImportModal: (jobInstance: ReferenceJob, isReference?: boolean) => void;
    removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(afterSave?: () => void): void;
    updateJob(jobInstance: ReferenceJob): Promise<boolean>;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: jobInstance,
                instance: { stopFrame },
            },
        },
    } = state;

    return {
        jobInstance: jobInstance as ReferenceJob,
        stopFrame,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        showExportModal(jobInstance: ReferenceJob): void {
            dispatch(exportActions.openExportDatasetModal(jobInstance));
        },
        showExportBlenderModal(jobInstance: ReferenceJob): void {
            dispatch(exportActions.openExportBlenderModal(jobInstance));
        },
        showImportModal(jobInstance: ReferenceJob, isReference = false): void {
            dispatch(importActions.openImportDatasetModal(jobInstance, isReference));
        },
        removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean) {
            dispatch(removeAnnotationsAsyncAction(startnumber, endnumber, delTrackKeyframesOnly));
        },
        setForceExitAnnotationFlag(forceExit: boolean): void {
            dispatch(setForceExitAnnotationFlagAction(forceExit));
        },
        saveAnnotations(afterSave?: () => void): void {
            dispatch(saveAnnotationsAsync(afterSave));
        },
        updateJob(jobInstance: ReferenceJob): Promise<boolean> {
            return dispatch(updateReferenceJobAsync(jobInstance));
        },
    };
}

type Props = StateToProps & DispatchToProps & RouteComponentProps;

function AnnotationMenuContainer(props: Props): JSX.Element {
    const {
        jobInstance,
        stopFrame,
        history,
        showExportModal,
        showExportBlenderModal,
        showImportModal,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
        updateJob,
    } = props;

    const onClickMenu = (params: MenuInfo): void => {
        const [action] = params.keyPath;
        if (action === Actions.EXPORT_JOB_DATASET) {
            showExportModal(jobInstance);
        } else if (action === Actions.EXPORT_JOB_BLENDER) {
            showExportBlenderModal(jobInstance);
        } else if (action === Actions.RENEW_JOB) {
            jobInstance.state = core.enums.ReferenceJobState.NEW;
            jobInstance.stage = ReferenceJobStage.ANNOTATION;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    message.info('Job renewed', 2);
                }
            });
        } else if (action === Actions.FINISH_JOB) {
            jobInstance.stage = ReferenceJobStage.ACCEPTANCE;
            jobInstance.state = core.enums.ReferenceJobState.COMPLETED;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    history.push(`/references/${jobInstance.referenceId}`);
                }
            });
        } else if (action === Actions.OPEN_REFERENCE) {
            history.push(`/references/${jobInstance.referenceId}`);
        } else if (action.startsWith('state:')) {
            [, jobInstance.state] = action.split(':') as [string, ReferenceJobState];
            updateJob(jobInstance).then((success) => {
                if (success) {
                    message.info('Job state updated', 2);
                }
            });
        } else if (action === Actions.LOAD_JOB_ANNO) {
            showImportModal(jobInstance);
        } else if (action === Actions.LOAD_JOB_REFERENCE) {
            showImportModal(jobInstance, true);
        }
    };

    return (
        <AnnotationMenuComponent
            taskMode={jobInstance.mode}
            onClickMenu={onClickMenu}
            removeAnnotations={removeAnnotations}
            setForceExitAnnotationFlag={setForceExitAnnotationFlag}
            saveAnnotations={saveAnnotations}
            jobInstance={jobInstance}
            stopFrame={stopFrame}
        />
    );
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AnnotationMenuContainer));
